import type { CSSProperties } from "react";
import { flexRender, type Header } from "@tanstack/react-table";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import { TableSortIcon } from "../tables";

export function TableHeaderCell<T>({ header }: { header: Header<T, unknown> }) {

  const isDragDisabled = header.column.id === "select";
  const { attributes, isDragging, listeners, setNodeRef, transform } =
    useSortable({
      id: header.column.id,
      disabled: isDragDisabled,
    });



  const borderColor =
    (header.column.columnDef.meta as any)?.borderColor || "rgb(204,0,0)";

  const style: CSSProperties = {
    opacity: isDragging ? 0.8 : 1,
    position: header.column.getIsPinned() ? "sticky" : "relative",
    left: header.column.getIsPinned()
      ? header.column.getStart("left")
      : undefined,
    zIndex: header.column.getIsPinned() ? 50 : 1,
    transform: CSS.Translate.toString(transform), // translate instead of transform to avoid squishing
    transition: "width transform 0.2s ease-in-out",
    whiteSpace: "nowrap",
    // borderLeft: header.column.getIsPinned() ? "6px solid red" : "",
    borderLeft: header.column.id === "select" ? `6px solid ${borderColor}` : "",
  };

  return (
    <div
      key={header.id}
      className={`flex items-center justify-between box-border border-r-1 border-b-1 bg-white
               border-b-gray-200 font-semibold hover:text-slate-900 border-t-1 border-gray-200 shadow-xs
              ${header.column.getIsResizing()
          ? "border-r-blue-600"
          : "border-r-gray-200"
        }`}
      style={style}
    >
      <div
        className={`flex items-center ${header.column.id === "select"
          ? "justify-center w-full px-0 py-0 cursor-default"
          : "justify-center w-[92%] px-3 py-1 cursor-grab"
          } group bg-white`}
        ref={!isDragDisabled ? setNodeRef : undefined}
        {...(!isDragDisabled ? attributes : {})}
        {...(!isDragDisabled ? listeners : {})}
      >
        {flexRender(header.column.columnDef.header, header.getContext())}
        {!isDragDisabled && (
          <GripVertical
            size={16}
            className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white"
          />
        )}

      </div>

      {header.column.id !== "select" && header.column.getCanSort() && (
        <div
          onClick={header.column.getToggleSortingHandler()}
          className={`absolute top-0 right-2 flex items-center h-full opacity-0 hover:opacity-100 transition-opacity cursor-pointer`}
        >
          <TableSortIcon size={14} column={header.column} />
        </div>
      )}

      <div
        {...{
          onDoubleClick: () => header.column.resetSize(),
          onMouseDown: header.getResizeHandler(),
          onTouchStart: header.getResizeHandler(),
          className: `absolute top-0 right-0 ml-4 w-2 h-full opacity-0 hover:opacity-100 cursor-col-resize transition-opacity duration-150 hover:bg-blue-400`,
        }}
      />
    </div>
  );
}
